import React from 'react';

import { tv } from '../../tv';

/**
 * A placeholder for content whose layout is known and whose data is not yet
 * here, so the content arrives without a jump. `Skeleton` wraps the real
 * content (with placeholder values) and takes its box; the shapes stand for
 * structure that has no content yet, on the system's own scales; `Region`
 * announces a loading area once. See loading.md.
 */

/* React 18 does not know `inert`; the empty string is the form it forwards untouched. */
const INERT = { inert: '' } as Record<string, string>;

const skeletonStyles = tv({
  slots: {
    /* inline-block so the wash hugs the content it stands for: a wrapped name
     * is a name-sized bar. The children keep their box and lose their paint
     * (visibility, not display, so the geometry stays), and the wrapper's own
     * text goes transparent for the case where the child is a bare string. */
    root: [
      'ck:relative ck:inline-block ck:max-w-full ck:align-top ck:select-none',
      'ck:text-transparent ck:animate-skeleton ck:rounded-2 ck:[&>*]:invisible',
    ],
  },
  variants: {
    /* Fills the parent's allocation, Stack's contract, for a chart body or a
     * card slot whose height is the region's rather than the content's. */
    filled: {
      true: { root: 'ck:block ck:size-full' },
      false: {},
    },
  },
  defaultVariants: { filled: false },
});

export interface SkeletonProps {
  /** While true the children are hidden under the wash; when false they render in place, unwrapped. */
  isLoading: boolean;
  /** The real content, with placeholder values where the data is not here yet. */
  children: React.ReactNode;
  /** Fill the parent's allocation instead of hugging the content. */
  isFilled?: boolean;
  id?: string;
}

const SkeletonRoot = React.forwardRef<HTMLSpanElement, SkeletonProps>(function Skeleton(
  { isLoading, isFilled = false, children, id },
  ref,
) {
  // oxlint-disable-next-line react/jsx-no-useless-fragment
  if (!isLoading) return <>{children}</>;
  const { root } = skeletonStyles({ filled: isFilled });
  return (
    <span
      ref={ref}
      id={id}
      className={root()}
      aria-hidden="true"
      data-component="skeleton"
      {...INERT}
    >
      {children}
    </span>
  );
});

/* Each line is a box of the step's line height with a bar of the step's
 * glyph height centred in it, so a placeholder paragraph has the rhythm of
 * the paragraph it stands for and a single line sits where its text will. */
const textStyles = tv({
  slots: {
    root: 'ck:flex ck:w-full ck:flex-col',
    line: 'ck:flex ck:w-full ck:items-center',
    bar: 'ck:w-full ck:animate-skeleton ck:rounded-2',
    last: 'ck:w-3/5',
  },
  variants: {
    size: {
      xs: { line: 'ck:h-4', bar: 'ck:h-3' },
      sm: { line: 'ck:h-5', bar: 'ck:h-3' },
      base: { line: 'ck:h-6', bar: 'ck:h-4' },
      lg: { line: 'ck:h-7', bar: 'ck:h-4' },
      xl: { line: 'ck:h-7', bar: 'ck:h-5' },
    },
  },
  defaultVariants: { size: 'base' },
});

export interface SkeletonTextProps {
  /** The type step the text will read at. */
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  /** How many lines the text will run to; the last of several runs short. */
  lines?: number;
  id?: string;
}

const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(function SkeletonText(
  { size = 'base', lines = 1, id },
  ref,
) {
  const { root, line, bar, last } = textStyles({ size });
  const count = Math.max(1, Math.floor(lines));
  return (
    <div ref={ref} id={id} className={root()} aria-hidden="true" data-component="skeleton-text">
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className={line()}>
          <span className={bar({ class: count > 1 && index === count - 1 ? last() : undefined })} />
        </span>
      ))}
    </div>
  );
});

const circleStyles = tv({
  base: 'ck:shrink-0 ck:animate-skeleton ck:rounded-full',
  variants: {
    size: { sm: 'ck:size-6', md: 'ck:size-8', lg: 'ck:size-10' },
  },
  defaultVariants: { size: 'md' },
});

export interface SkeletonCircleProps {
  /** The avatar ramp: 24, 32 or 40px. */
  size?: 'sm' | 'md' | 'lg';
  id?: string;
}

const SkeletonCircle = React.forwardRef<HTMLSpanElement, SkeletonCircleProps>(
  function SkeletonCircle({ size = 'md', id }, ref) {
    return (
      <span
        ref={ref}
        id={id}
        className={circleStyles({ size })}
        aria-hidden="true"
        data-component="skeleton-circle"
      />
    );
  },
);

const blockStyles = tv({
  base: 'ck:block ck:w-full ck:animate-skeleton ck:rounded-3',
  variants: {
    size: { sm: 'ck:h-6', md: 'ck:h-8', lg: 'ck:h-10' },
    filled: { true: 'ck:size-full', false: '' },
  },
  defaultVariants: { size: 'md', filled: false },
});

export interface SkeletonBlockProps {
  /** A control-shaped bar, full width, on the 24/32/40 ramp. */
  size?: 'sm' | 'md' | 'lg';
  /** Fill the parent's allocation instead of a step. */
  isFilled?: boolean;
  id?: string;
}

const SkeletonBlock = React.forwardRef<HTMLSpanElement, SkeletonBlockProps>(function SkeletonBlock(
  { size = 'md', isFilled = false, id },
  ref,
) {
  return (
    <span
      ref={ref}
      id={id}
      className={blockStyles({ size, filled: isFilled })}
      aria-hidden="true"
      data-component="skeleton-block"
    />
  );
});

const regionStyles = tv({ slots: { root: 'ck:contents', label: 'ck:sr-only' } });

export interface SkeletonRegionProps {
  /** What the region is loading, announced once: "Loading vendor". */
  label: string;
  children: React.ReactNode;
  id?: string;
}

/* One announcement for the region: a status carrying the label as hidden text
 * (a live region announces content, not its aria-label), over placeholders
 * that are otherwise silent. display: contents adds no box to the layout. */
const SkeletonRegion = React.forwardRef<HTMLDivElement, SkeletonRegionProps>(
  function SkeletonRegion({ label, children, id }, ref) {
    const { root, label: hidden } = regionStyles();
    return (
      <div ref={ref} id={id} role="status" className={root()} data-component="skeleton-region">
        <span className={hidden()}>{label}</span>
        {children}
      </div>
    );
  },
);

export const Skeleton = Object.assign(SkeletonRoot, {
  Text: SkeletonText,
  Circle: SkeletonCircle,
  Block: SkeletonBlock,
  Region: SkeletonRegion,
});
